import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator } from "@frui.ts/validation";
import type UnpairedPayment from "entities/unpairedPayment";
import type { interfaces } from "inversify";
import { observable } from "mobx";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, { paymentPaired } from "services/eventBus";
import LocalizationService from "services/localizationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import NotificationService, { NotificationType } from "services/notificationService";
import ChargeOrderSearchResponse from "entities/chargeOrderSearchResponse";
import ChargeOrderSearchFilter, { ChargeOrderSearchFilterValidationRules } from "entities/filters/chargeOrderSearchFilter";
import PaymentsPairingRepository from "repositories/paymentsPairingRepository";
import type ChargeOrderStatus from "entities/chargeOrderStatus";
import type SourceType from "entities/sourceType";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import ChargeOrderSearchListViewModel from "../chargeOrderSearchListViewModel";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

export default class PairingByPaymentOrdersViewModel extends ChargeOrderSearchListViewModel {
  @observable openOrder?: ChargeOrderSearchResponse;

  constructor(
    private payment: UnpairedPayment,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: PaymentsPairingRepository,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    protected optionsService: OptionsService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(countrySelectViewModelFactory);
    this.navigationName = "";
  }

  @bound
  async onPairOrderClick(order: ChargeOrderSearchResponse) {
    if (!this.allowToPair) {
      this.notificationService.addNotAllowedError();
      return;
    }

    this.openOrder = order;
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmPaymentMatch.title"), {
      confirmButton: { text: this.translate("confirmPaymentMatch.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.pairPaymentWithOrder(this.payment, order);
        this.notificationService.add({ title: this.translate("paired"), type: NotificationType.SUCCESS });
        this.eventBus.publish(paymentPaired({ payment: this.payment, order }));
      } catch (e) {
        // General error handle is good enough
      }
    }
    this.openOrder = undefined;
  }

  protected processData(items: ChargeOrderSearchResponse[]) {
    return this.enumService.proxyObjectEnums(items);
  }

  protected createFilter() {
    return attachAutomaticValidator(new ChargeOrderSearchFilter(), ChargeOrderSearchFilterValidationRules);
  }

  translateState(status: ChargeOrderStatus) {
    return this.localizationService.translateEnum("chargeOrderStatus", status);
  }

  translateChargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  translateSourceType(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  static Factory({ container }: interfaces.Context) {
    return (payment: UnpairedPayment) =>
      new PairingByPaymentOrdersViewModel(
        payment,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(PaymentsPairingRepository),
        container.get(ConfirmationService),
        container.get(NotificationService),
        container.get(EnumService),
        container.get(PriceListService),
        container.get(OptionsService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
